import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	IconButton,
	MenuItem,
	Paper,
	Snackbar,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { MessageSquareQuote, Pencil, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../config/supabase";

const TABLE_NAME = "testimonials";
const STORAGE_BUCKET = "testimonials";

const emptyForm = {
	quote: "",
	author_name: "",
	author_title: "",
	rating: 5,
	is_active: true,
	sort_order: 0,
};

const publicUrlFor = (filePath) => {
	if (!filePath) return "";
	const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(filePath);
	return data?.publicUrl || "";
};

const starsLabel = (rating) => "★".repeat(Math.max(1, Math.min(5, Number(rating) || 5)));

const Testimonials = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [photoFile, setPhotoFile] = useState(null);
	const [photoPreview, setPhotoPreview] = useState("");
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const showMsg = (message, severity = "success") =>
		setSnackbar({ open: true, message, severity });

	const fetchRows = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from(TABLE_NAME)
				.select("*")
				.order("sort_order", { ascending: true })
				.order("created_at", { ascending: true });
			if (error) throw error;
			setRows(data || []);
		} catch (err) {
			console.error("Failed to load testimonials:", err);
			showMsg(err.message || "Failed to load testimonials", "error");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	useEffect(() => {
		if (!photoFile) return undefined;
		const url = URL.createObjectURL(photoFile);
		setPhotoPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [photoFile]);

	const openCreate = () => {
		setEditing(null);
		setForm({ ...emptyForm, sort_order: rows.length });
		setPhotoFile(null);
		setPhotoPreview("");
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditing(row);
		setForm({
			quote: row.quote || "",
			author_name: row.author_name || "",
			author_title: row.author_title || "",
			rating: row.rating ?? 5,
			is_active: row.is_active !== false,
			sort_order: row.sort_order ?? 0,
		});
		setPhotoFile(null);
		setPhotoPreview(publicUrlFor(row.file_path));
		setDialogOpen(true);
	};

	const onPickPhoto = (fileList) => {
		const file = Array.from(fileList || []).find((f) =>
			f.type.startsWith("image/"),
		);
		if ((fileList?.length || 0) > 0 && !file) {
			showMsg("Only image files are allowed", "warning");
			return;
		}
		setPhotoFile(file || null);
	};

	const uploadPhoto = async (file) => {
		const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
		const filePath = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
		const { error } = await supabase.storage
			.from(STORAGE_BUCKET)
			.upload(filePath, file, {
				contentType: file.type,
				upsert: false,
			});
		if (error) throw error;
		return { filePath, fileName: file.name };
	};

	const handleSave = async () => {
		if (!form.quote.trim() || !form.author_name.trim()) {
			showMsg("Quote and author name are required", "warning");
			return;
		}
		if (!editing && !photoFile) {
			showMsg("Please upload an author photo", "warning");
			return;
		}

		setSaving(true);
		let uploadedPath = null;
		try {
			const payload = {
				quote: form.quote.trim(),
				author_name: form.author_name.trim(),
				author_title: (form.author_title || "").trim(),
				rating: Math.max(1, Math.min(5, Number(form.rating) || 5)),
				is_active: !!form.is_active,
				sort_order: Number.isFinite(Number(form.sort_order))
					? Number(form.sort_order)
					: 0,
			};

			if (photoFile) {
				const uploaded = await uploadPhoto(photoFile);
				uploadedPath = uploaded.filePath;
				payload.file_path = uploaded.filePath;
				payload.file_name = uploaded.fileName;
			}

			if (editing) {
				const { error } = await supabase
					.from(TABLE_NAME)
					.update(payload)
					.eq("id", editing.id);
				if (error) throw error;

				if (uploadedPath && editing.file_path) {
					await supabase.storage
						.from(STORAGE_BUCKET)
						.remove([editing.file_path]);
				}
				showMsg("Testimonial updated");
			} else {
				const { error } = await supabase.from(TABLE_NAME).insert(payload);
				if (error) throw error;
				showMsg("Testimonial added");
			}

			setDialogOpen(false);
			setLoading(true);
			await fetchRows();
		} catch (err) {
			console.error("Save failed:", err);
			if (uploadedPath) {
				await supabase.storage.from(STORAGE_BUCKET).remove([uploadedPath]);
			}
			showMsg(err.message || "Failed to save testimonial", "error");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!editing) return;
		setSaving(true);
		try {
			const { error } = await supabase
				.from(TABLE_NAME)
				.delete()
				.eq("id", editing.id);
			if (error) throw error;

			if (editing.file_path) {
				await supabase.storage
					.from(STORAGE_BUCKET)
					.remove([editing.file_path]);
			}

			showMsg("Testimonial deleted");
			setDeleteOpen(false);
			setEditing(null);
			setLoading(true);
			await fetchRows();
		} catch (err) {
			console.error("Delete failed:", err);
			showMsg(err.message || "Failed to delete testimonial", "error");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<MessageSquareQuote size={28} />
					<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
						Testimonials
					</Typography>
				</Stack>
				<Button
					variant="contained"
					startIcon={<Plus size={16} />}
					onClick={openCreate}>
					Add testimonial
				</Button>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Manage the &quot;Principals who said yes first&quot; marquee on the
				Answers page. Inactive entries stay hidden on the public site.
			</Typography>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : (
				<Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
					{rows.length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center" }}>
							<Typography color="text.secondary">
								No testimonials yet. Add the first quote.
							</Typography>
						</Box>
					) : (
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Author</TableCell>
										<TableCell>Quote</TableCell>
										<TableCell>Rating</TableCell>
										<TableCell>Active</TableCell>
										<TableCell align="right">Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.sort_order}</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1.5} alignItems="center">
													<Avatar
														src={publicUrlFor(row.file_path)}
														alt={row.author_name}
														sx={{ width: 40, height: 40 }}
													/>
													<Box>
														<Typography variant="body2" fontWeight={600}>
															{row.author_name}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															{row.author_title || "—"}
														</Typography>
													</Box>
												</Stack>
											</TableCell>
											<TableCell sx={{ maxWidth: 360 }}>
												<Typography
													variant="body2"
													sx={{
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														overflow: "hidden",
													}}>
													{row.quote}
												</Typography>
											</TableCell>
											<TableCell>{starsLabel(row.rating)}</TableCell>
											<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
											<TableCell align="right">
												<IconButton
													aria-label="Edit"
													onClick={() => openEdit(row)}>
													<Pencil size={16} />
												</IconButton>
												<IconButton
													color="error"
													aria-label="Delete"
													onClick={() => {
														setEditing(row);
														setDeleteOpen(true);
													}}>
													<Trash2 size={16} />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			)}

			<Dialog
				open={dialogOpen}
				onClose={() => !saving && setDialogOpen(false)}
				fullWidth
				maxWidth="sm">
				<DialogTitle>
					{editing ? "Edit testimonial" : "Add testimonial"}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Quote"
							value={form.quote}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, quote: e.target.value }))
							}
							fullWidth
							required
							multiline
							minRows={3}
						/>
						<TextField
							label="Author name"
							value={form.author_name}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, author_name: e.target.value }))
							}
							fullWidth
							required
						/>
						<TextField
							label="Title / school"
							value={form.author_title}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, author_title: e.target.value }))
							}
							helperText="Shown under the name, e.g. Principal, BenchMark International SS School, Tirur"
							fullWidth
						/>
						<TextField
							select
							label="Star rating"
							value={form.rating}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, rating: Number(e.target.value) }))
							}
							fullWidth>
							{[5, 4, 3, 2, 1].map((n) => (
								<MenuItem key={n} value={n}>
									{starsLabel(n)} ({n})
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Display order"
							type="number"
							value={form.sort_order}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, sort_order: e.target.value }))
							}
							fullWidth
						/>
						<FormControlLabel
							control={
								<Switch
									checked={!!form.is_active}
									onChange={(e) =>
										setForm((prev) => ({
											...prev,
											is_active: e.target.checked,
										}))
									}
								/>
							}
							label="Active on website"
						/>
						<Box>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Author photo {editing ? "(optional replace)" : "*"}
							</Typography>
							<Stack direction="row" spacing={2} alignItems="center">
								<Avatar
									src={photoPreview}
									alt="Preview"
									sx={{ width: 56, height: 56 }}
								/>
								<Button variant="outlined" component="label">
									{photoFile || photoPreview ? "Change photo" : "Upload photo"}
									<input
										hidden
										type="file"
										accept="image/*"
										onChange={(e) => onPickPhoto(e.target.files)}
									/>
								</Button>
							</Stack>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={
							saving || !form.quote.trim() || !form.author_name.trim()
						}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteOpen}
				onClose={() => !saving && setDeleteOpen(false)}>
				<DialogTitle>Delete testimonial?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This removes the quote from <strong>{editing?.author_name}</strong>{" "}
						on the website.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDelete}
						disabled={saving}>
						{saving ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Testimonials;
